import {
  apiCall,
  collectPaginatedCatalog,
  type ApiClientConfig,
} from "@playbookai/api-clients";
import type { KpiDef, KpiStatus, PaginatedResponse } from "@playbookai/types";

export interface ManagedKpiSummaryWire {
  readonly kpiId: string;
  readonly name: string;
  readonly unit: string;
  readonly direction: "HIGHER_IS_BETTER" | "LOWER_IS_BETTER" | "TARGET_RANGE";
  readonly lifecycleStatus: "draft" | "active" | "archived";
  readonly currentState: {
    readonly performanceStatus: string;
    readonly currentValue: string | null;
  } | null;
}

const STATUS_BY_PERFORMANCE: Record<string, KpiStatus> = {
  green: "healthy",
  amber: "watch",
  red: "critical",
};

export function toKpiDef(row: ManagedKpiSummaryWire): KpiDef {
  const value =
    row.lifecycleStatus === "active"
      ? (row.currentState?.currentValue ?? "—")
      : "—";
  const numeric = Number(value);
  return {
    id: row.kpiId,
    name: row.name,
    value,
    numeric: Number.isFinite(numeric) ? numeric : 0,
    ...(row.unit ? { unit: row.unit } : {}),
    status:
      row.lifecycleStatus === "active"
        ? (STATUS_BY_PERFORMANCE[row.currentState?.performanceStatus ?? ""] ??
          "none")
        : "none",
    delta: "",
    deltaNumeric: 0,
    ...(row.direction === "LOWER_IS_BETTER" ? { inverse: true } : {}),
    series: [],
    threshold: "",
    commentary: "",
  };
}

export interface KpiCatalogPage {
  readonly items: readonly KpiDef[];
  readonly nextCursor: string | null;
}

/** One active-only page for a lazy KPI picker. */
export async function listKpiCatalogPage(
  config: ApiClientConfig,
  params: {
    readonly q?: string;
    readonly cursor?: string;
    readonly limit?: number;
    readonly signal?: AbortSignal;
  } = {},
): Promise<KpiCatalogPage> {
  const page = await apiCall<PaginatedResponse<ManagedKpiSummaryWire>>(
    config,
    "GET",
    "/v1/kpis",
    undefined,
    {
      q: params.q,
      lifecycleStatus: "active",
      cursor: params.cursor,
      limit: params.limit ?? 20,
    },
    params.signal,
  );
  if (page.hasMore && !page.nextCursor) {
    throw new Error("KPI catalogue returned hasMore without a cursor.");
  }
  return { items: page.data.map(toKpiDef), nextCursor: page.nextCursor };
}

/** Resolve a persisted selection even when it is now archived. */
export async function getKpiCatalogItem(
  config: ApiClientConfig,
  kpiId: string,
): Promise<{ readonly kpi: KpiDef; readonly archived: boolean }> {
  const row = await apiCall<ManagedKpiSummaryWire>(
    config,
    "GET",
    `/v1/kpis/${encodeURIComponent(kpiId)}`,
  );
  return { kpi: toKpiDef(row), archived: row.lifecycleStatus === "archived" };
}

/** Full catalogue retained for existing non-picker consumers. */
export async function listKpiCatalog(
  config: ApiClientConfig,
): Promise<KpiDef[]> {
  return collectPaginatedCatalog<ManagedKpiSummaryWire, KpiDef>({
    config,
    path: "/v1/kpis",
    map: toKpiDef,
    label: "KPI catalogue",
  });
}
